import {type FC, useMemo} from 'react';

import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';

import type {MembershipApiModel} from '../../../../api/membership';
import type {Props} from './types';

import {membershipStore} from '../../../../api/membership';
import {not} from '../../../../utils/conditionalUtils';
import {CourseModel} from '../../../../utils/entity/course/createCourseModel';
import {isCourseAvailableToJoin} from '../../../../utils/entity/course/isCourseAvailableToJoin';
import {logger} from '../../../../utils/logger';
import {platform} from '../../../../utils/platform';
import {isDefined} from '../../../../utils/typecheck';
import {CourseListItemJSX} from '../../components/CourseListItem';
import {getButtons} from './getButtons';

export const ClientGroupCourseListItem: FC<Props> = ({
  course,
  onClick,
  onJoinClick,
}) => {
  const {t} = useI18n();
  const model = useMemo(() => new CourseModel(course), [course]);

  const membership = membershipStore.membershipLookup.get(course._id);

  const getStatus = () => {
    if (platform.isNative) {
      if (
        membership?.status === 'active' ||
        membership?.status === 'accepted'
      ) {
        return t('shared:joined');
      } else if (membership?.status === 'requested') {
        return t('shared:request_sent');
      } else if (!membership && isCourseAvailableToJoin(course)) {
        return model.isAutoAccept
          ? t('shared:join_now')
          : t('shared:request_to_join');
      }
    }

    return model.statusString;
  };

  const getClickHandler = () => {
    const shouldAllowClick = isDefined(membership)
      ? (
          [
            'accepted',
            'active',
            'archived',
            'frozen',
          ] as MembershipApiModel['status'][]
        ).includes(membership?.status)
      : false;

    if (shouldAllowClick) {
      return () => onClick?.(course);
    } else if (
      platform.isNative &&
      isCourseAvailableToJoin(course) &&
      not(membership?.status === 'requested') &&
      not(membership?.status === 'proposed')
    ) {
      return () => {
        if (!model.isAutoAccept) {
          logger.event('request_join_tap', {
            program_type: 'group',
          });
        }

        onJoinClick(course);
      };
    }
  };

  return (
    <Observer>
      {() => (
        <CourseListItemJSX
          buttons={getButtons({
            course,
            isAutoAccept: model.isAutoAccept,
            onClick,
            onJoinClick,
          })}
          clientsCount={model.clientsCount}
          groupSize={model.groupSize ?? 0}
          isArchived={model.isArchived}
          onClick={getClickHandler()}
          status={getStatus()}
          title={model.label}
          withClockIcon={
            platform.isNative && membership?.status === 'requested'
          }
        />
      )}
    </Observer>
  );
};
